import { useMemo, useState } from "react";
import { Ban, Building2, CheckCircle, Edit3, Loader2, Pause, Plus, Play, Search, Trash2, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useCreateDomain,
    useCreateTenant,
    useCreateTenantUser,
    useDeleteDomain,
    useDeleteTenantUser,
    useDomains,
    useMe,
    useSetDomainSeatLimit,
    useSetDomainSecurity,
    useTenantLifecycle,
    useTenantStorage,
    useTenantUsers,
    useTenants,
    useUpdateTenant,
    useUpdateTenantUser,
    useVerifyDomain
} from "@/api/hooks";
import type { MailDomain, Me, Tenant, TenantUser } from "@/api/adminBff";
import { SUITE_APPS } from "@/api/adminBff";

export function TenantsPage() {
    const me = useMe();
    const tenants = useTenants();
    const create = useCreateTenant();
    const update = useUpdateTenant();
    const lifecycle = useTenantLifecycle();

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = tenants.data?.find((t) => t.id === selectedId) ?? null;

    const [filter, setFilter] = useState("");
    const visibleTenants = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const all = tenants.data ?? [];
        if (q.length === 0) return all;
        return all.filter((t) =>
            t.displayName.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
        );
    }, [filter, tenants.data]);

    const [draft, setDraft] = useState({ slug: "", displayName: "", seatLimit: 25 });

    const handleCreate = async () => {
        if (!draft.slug || !draft.displayName) return;
        const created = await create.mutateAsync({
            slug: draft.slug,
            displayName: draft.displayName,
            seatLimit: draft.seatLimit,
            storageQuotaBytes: 10 * 1024 * 1024 * 1024
        });
        setSelectedId(created.id);
        setDraft({ slug: "", displayName: "", seatLimit: 25 });
    };

    return (
        <div className="mx-auto max-w-6xl">
            <header className="mb-4 flex items-center gap-2">
                <Building2 className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold">Tenants</h1>
            </header>

            {me.data?.isSuperAdmin ? (
            <section className="mb-6 rounded-lg border bg-card p-4 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold">New tenant</h2>
                <div className="grid grid-cols-4 gap-2">
                    <Input
                        placeholder="slug (a-z 0-9 -)"
                        value={draft.slug}
                        onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value.toLowerCase() }))}
                    />
                    <Input
                        placeholder="Display name"
                        value={draft.displayName}
                        onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                    />
                    <Input
                        type="number"
                        min={1}
                        value={draft.seatLimit}
                        onChange={(e) => setDraft((d) => ({ ...d, seatLimit: Number(e.target.value) || 1 }))}
                    />
                    <Button onClick={handleCreate} disabled={create.isPending}>
                        {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        Create
                    </Button>
                </div>
                {create.isError ? <p className="mt-2 text-xs text-destructive">{create.error?.message}</p> : null}
            </section>
            ) : null}

            <div className="grid grid-cols-[260px_1fr] gap-4">
                <aside className="rounded-lg border bg-card p-2">
                    <div className="relative mb-2">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <Input
                            type="search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter tenants"
                            aria-label="Filter tenants by name or slug"
                            className="h-8 pl-7 text-sm"
                        />
                    </div>
                    {tenants.isPending ? (
                        <p className="p-3 text-sm text-muted-foreground">Loading…</p>
                    ) : visibleTenants.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                            {visibleTenants.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setSelectedId(t.id)}
                                    className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent ${
                                        selectedId === t.id ? "bg-accent" : ""
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium">{t.displayName}</div>
                                        <div className="truncate text-xs text-muted-foreground">{t.slug}</div>
                                    </div>
                                    <StatusBadge status={t.status} />
                                </button>
                            ))}
                        </div>
                    ) : tenants.data && tenants.data.length > 0 ? (
                        <p className="p-3 text-sm text-muted-foreground">No tenants match &ldquo;{filter}&rdquo;.</p>
                    ) : (
                        <p className="p-3 text-sm text-muted-foreground">No tenants yet.</p>
                    )}
                </aside>

                {selected ? (
                    <TenantDetail
                        key={selected.id}
                        tenant={selected}
                        me={me.data}
                        onUpdate={(payload) => update.mutate({ id: selected.id, payload })}
                        onLifecycle={(action) => lifecycle.mutate({ id: selected.id, action })}
                    />
                ) : (
                    <section className="flex items-center justify-center rounded-lg border bg-card p-10 text-sm text-muted-foreground">
                        Select a tenant to manage users, domains, and settings.
                    </section>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: Tenant["status"] }) {
    const cls =
        status === "Active"
            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
            : status === "Suspended"
            ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
            : "bg-muted text-muted-foreground";
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>{status}</span>;
}

function TenantDetail({
    tenant,
    me,
    onUpdate,
    onLifecycle
}: {
    tenant: Tenant;
    me: Me | undefined;
    onUpdate: (payload: { slug: string; displayName: string; description?: string | null; seatLimit: number; storageQuotaBytes: number }) => void;
    onLifecycle: (action: "suspend" | "reactivate" | "delete") => void;
}) {
    // Keyed on tenant.id so switching tenants re-seeds the editor from the
    // freshly-selected row rather than leaking the previous tenant's values.
    const [displayName, setDisplayName] = useState(tenant.displayName);
    const [seatLimit, setSeatLimit] = useState(tenant.seatLimit);
    const [description, setDescription] = useState(tenant.description ?? "");
    const [quotaGb, setQuotaGb] = useState(() => bytesToGb(tenant.storageQuotaBytes));

    // Changing rows in the left list triggers a fresh mount via the `key`
    // on <TenantDetail>, so no effect is needed to reset state.

    const dirty =
        displayName !== tenant.displayName ||
        seatLimit !== tenant.seatLimit ||
        (description || null) !== (tenant.description ?? null) ||
        gbToBytes(quotaGb) !== tenant.storageQuotaBytes;

    return (
        <section className="space-y-4">
            {me?.isSuperAdmin ? (
            <div className="rounded-lg border bg-card p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold">Settings</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium text-muted-foreground">Display name</span>
                        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium text-muted-foreground">Seat limit</span>
                        <Input
                            type="number"
                            min={1}
                            value={seatLimit}
                            onChange={(e) => setSeatLimit(Number(e.target.value) || 1)}
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium text-muted-foreground">Storage quota (GB)</span>
                        <Input
                            type="number"
                            min={1}
                            step={1}
                            value={quotaGb}
                            onChange={(e) => setQuotaGb(Number(e.target.value) || 1)}
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-xs sm:col-span-2">
                        <span className="font-medium text-muted-foreground">Description</span>
                        <textarea
                            className="min-h-[60px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional — shown in admin lists and exports."
                        />
                    </label>
                </div>
                <div className="mt-3 flex items-center justify-between">
                    <div className="flex gap-2">
                        {tenant.status === "Active" ? (
                            <Button variant="outline" size="sm" onClick={() => onLifecycle("suspend")}>
                                <Pause className="h-4 w-4" /> Suspend
                            </Button>
                        ) : (
                            <Button variant="outline" size="sm" onClick={() => onLifecycle("reactivate")}>
                                <Play className="h-4 w-4" /> Reactivate
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            onClick={() => {
                                if (window.confirm(`Delete tenant "${tenant.slug}"?`)) onLifecycle("delete");
                            }}
                        >
                            <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                    </div>
                    <Button
                        size="sm"
                        disabled={!dirty}
                        onClick={() =>
                            onUpdate({
                                slug: tenant.slug,
                                displayName,
                                description: description.trim().length > 0 ? description : null,
                                seatLimit,
                                storageQuotaBytes: gbToBytes(quotaGb)
                            })
                        }
                    >
                        <Save className="h-4 w-4" />
                        Save
                    </Button>
                </div>
            </div>
            ) : null}

            <StorageCard tenantId={tenant.id} quotaBytes={gbToBytes(quotaGb)} />
            <DomainUsersSection tenantId={tenant.id} me={me} />
        </section>
    );
}

function StorageCard({ tenantId, quotaBytes }: { tenantId: string; quotaBytes: number }) {
    const storage = useTenantStorage(tenantId);
    const data = storage.data ?? null;

    // Prefer the in-flight editor value for the quota denominator so the
    // bar updates live while the admin types a new quota (they haven't
    // clicked Save yet). Server value wins once saved.
    const denominator = data?.quotaBytes ?? quotaBytes;
    const used = data?.usedBytes ?? 0;
    const unavailable = !!data && !data.available;
    const percent = unavailable || denominator <= 0 ? 0 : Math.min(100, Math.round((used / denominator) * 100));
    const barColor =
        percent >= 90 ? "bg-destructive"
        : percent >= 75 ? "bg-amber-500"
        : "bg-emerald-500";

    return (
        <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Storage usage</h3>
                <span className="text-xs text-muted-foreground">
                    {storage.isPending
                        ? "Loading…"
                        : unavailable
                            ? "Backend unavailable"
                            : `${data?.objectCount ?? 0} object${(data?.objectCount ?? 0) === 1 ? "" : "s"}`}
                </span>
            </div>
            <div
                className="h-3 w-full overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                aria-label={`Storage used: ${percent}% of quota`}
            >
                <div
                    className={`h-full transition-all ${barColor}`}
                    style={{ width: `${percent}%` }}
                />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                    {unavailable ? "—" : formatBytes(used)} of {formatBytes(denominator)}
                </span>
                <span>{unavailable ? "" : `${percent}%`}</span>
            </div>
            {percent >= 90 && !unavailable ? (
                <p className="mt-2 text-xs text-destructive">
                    Approaching quota. Raise the limit above or archive old attachments.
                </p>
            ) : null}
        </div>
    );
}

function formatBytes(bytes: number): string {
    if (bytes < 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
}

function bytesToGb(bytes: number): number {
    return Math.max(1, Math.round(bytes / (1024 * 1024 * 1024)));
}
function gbToBytes(gb: number): number {
    return Math.max(1, gb) * 1024 * 1024 * 1024;
}

const GIB = 1024 * 1024 * 1024;
// Per-mailbox quota helpers allow fractional GB (e.g. 0.5) and don't clamp to a
// 1 GB floor the way the tenant-level quota does — an admin may want a 0 (block)
// or sub-GB plan. Trim trailing zeros so "1" shows instead of "1.00".
function bytesToGbStr(bytes: number): string {
    return Number((bytes / GIB).toFixed(2)).toString();
}
function gbStrToBytes(gb: string): number {
    const n = Number(gb);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * GIB);
}
function seedLicenceDraft(meta: MailDomain): { seats: string; tier: string; mailGb: string; fileGb: string; apps: Set<string> } {
    return {
        seats: meta.seatLimit === null ? "" : String(meta.seatLimit),
        tier: meta.planTier ?? "",
        mailGb: bytesToGbStr(meta.mailQuotaBytesPerUser),
        fileGb: bytesToGbStr(meta.fileQuotaBytesPerUser),
        apps: new Set(
            (meta.enabledApps ?? "")
                .split(",")
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean)
        )
    };
}

// Base roles must match what the Identity service defines — user creation is
// proxied there and an unknown role is silently dropped. Only a SuperAdmin
// may grant SuperAdmin.
const BASE_ROLES = ["User", "TenantAdmin"];

/**
 * Tenant → domain → mailbox hierarchy. Every user is grouped under their
 * email domain; each domain shows its seat usage. Domain admins only ever
 * receive their own domain from the BFF, so the grouping collapses to one
 * card for them.
 */
function DomainUsersSection({ tenantId, me }: { tenantId: string; me: Me | undefined }) {
    const users = useTenantUsers(tenantId);
    const domains = useDomains(tenantId);
    const createDomain = useCreateDomain(tenantId);
    const [newDomain, setNewDomain] = useState("");

    const groups = useMemo(() => {
        const byDomain = new Map<string, TenantUser[]>();
        for (const u of users.data ?? []) {
            const d = u.email.split("@")[1]?.toLowerCase() ?? "unknown";
            const arr = byDomain.get(d) ?? [];
            arr.push(u);
            byDomain.set(d, arr);
        }
        const known = (domains.data ?? []).map((d) => ({
            key: d.domain.toLowerCase(),
            meta: d as MailDomain | null,
            domain: d.domain.toLowerCase(),
            users: byDomain.get(d.domain.toLowerCase()) ?? []
        }));
        for (const g of known) byDomain.delete(g.key);
        // Users whose domain has no mail_domains row yet — still shown so
        // nothing silently disappears.
        const orphans = [...byDomain.entries()].map(([domain, us]) => ({
            key: domain, meta: null as MailDomain | null, domain, users: us
        }));
        return [...known, ...orphans];
    }, [users.data, domains.data]);

    const totalUsers = users.data?.length ?? 0;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                    Users ({totalUsers} total · {groups.length} domain{groups.length === 1 ? "" : "s"})
                </h3>
                {me?.isSuperAdmin ? (
                    <div className="flex gap-2">
                        <Input
                            className="h-8 w-48 text-sm"
                            placeholder="acme.example"
                            value={newDomain}
                            onChange={(e) => setNewDomain(e.target.value)}
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                if (!newDomain) return;
                                createDomain.mutate({ domain: newDomain }, { onSuccess: () => setNewDomain("") });
                            }}
                            disabled={createDomain.isPending || !newDomain}
                        >
                            <Plus className="h-4 w-4" /> Add domain
                        </Button>
                    </div>
                ) : null}
            </div>
            {createDomain.isError ? <p className="text-xs text-destructive">{createDomain.error?.message}</p> : null}
            {users.isPending || domains.isPending ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
            ) : groups.length > 0 ? (
                groups.map((g) => (
                    <DomainGroup key={g.key} tenantId={tenantId} domain={g.domain} meta={g.meta} users={g.users} me={me} />
                ))
            ) : (
                <p className="text-xs text-muted-foreground">No domains or users yet.</p>
            )}
        </div>
    );
}

/**
 * SuperAdmin-only per-domain security controls: the office-network allowlist
 * (IP/CIDR) and the native IMAP/SMTP toggle. Collapsed by default so it doesn't
 * clutter the common licence/seat workflow.
 */
function DomainSecuritySection({ tenantId, meta }: { tenantId: string; meta: MailDomain }) {
    const setSecurity = useSetDomainSecurity(tenantId);
    const [open, setOpen] = useState(false);
    const [cidrs, setCidrs] = useState(meta.accessAllowedCidrs ?? "");
    const [allowImap, setAllowImap] = useState(meta.allowImapSmtp);
    const [mfaMethods, setMfaMethods] = useState(meta.mfaMethods ?? "both");
    const [requireMfa, setRequireMfa] = useState(meta.requireMfa ?? false);

    const dirty = cidrs.trim() !== (meta.accessAllowedCidrs ?? "").trim()
        || allowImap !== meta.allowImapSmtp
        || mfaMethods !== (meta.mfaMethods ?? "both")
        || requireMfa !== (meta.requireMfa ?? false);
    const restricted = (meta.accessAllowedCidrs ?? "").trim().length > 0;

    return (
        <div className="mb-3 rounded-md border bg-muted/20 p-3">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
                <span>Security</span>
                <span className="flex items-center gap-2 normal-case">
                    <span className={restricted ? "text-amber-600" : "text-muted-foreground"}>
                        {restricted ? "network-restricted" : "any network"}
                    </span>
                    <span className={meta.allowImapSmtp ? "text-muted-foreground" : "text-amber-600"}>
                        · IMAP/SMTP {meta.allowImapSmtp ? "on" : "off"}
                    </span>
                    <span aria-hidden>{open ? "▲" : "▼"}</span>
                </span>
            </button>

            {open ? (
                <div className="mt-3 space-y-3">
                    <div>
                        <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                            Office-network allowlist (IP / CIDR)
                        </label>
                        <textarea
                            value={cidrs}
                            onChange={(e) => setCidrs(e.target.value)}
                            rows={2}
                            placeholder="e.g. 203.0.113.0/24, 198.51.100.7  — leave empty for no restriction"
                            className="w-full rounded-md border bg-background p-2 text-sm"
                        />
                        <p className="mt-1 text-[10px] text-muted-foreground">
                            When set, webmail access from this domain is allowed only from these
                            addresses. <strong>Empty = no restriction.</strong> This applies to everyone
                            on the domain, including admins — make sure your own network is included.
                        </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={allowImap}
                            onChange={(e) => setAllowImap(e.target.checked)}
                        />
                        Allow native IMAP/SMTP clients (Outlook, iPhone Mail, Thunderbird)
                    </label>
                    <p className="-mt-1 text-[10px] text-muted-foreground">
                        Webmail is unaffected. Turning this off blocks native mail apps from
                        signing in to mailboxes on this domain.
                    </p>

                    <div>
                        <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                            Allowed two-factor / sign-in methods
                        </label>
                        <select
                            value={mfaMethods}
                            onChange={(e) => setMfaMethods(e.target.value)}
                            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                        >
                            <option value="both">Both — authenticator app &amp; biometric passkeys</option>
                            <option value="authenticator">Authenticator app only (TOTP codes)</option>
                            <option value="biometric">Biometric passkeys only (Face/Touch/Hello)</option>
                        </select>
                        <p className="mt-1 text-[10px] text-muted-foreground">
                            Controls which methods users on this domain can set up. Default is both.
                        </p>
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={requireMfa}
                            onChange={(e) => setRequireMfa(e.target.checked)}
                        />
                        Require MFA — a password alone can’t sign in
                    </label>
                    <p className="-mt-1 text-[10px] text-muted-foreground">
                        Users must complete a second factor (authenticator or passkey) or set one
                        up on first sign-in. Applies to everyone on this domain.
                    </p>
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            className="h-7 px-3 text-xs"
                            disabled={!dirty || setSecurity.isPending}
                            onClick={() =>
                                setSecurity.mutate({ domainId: meta.id, accessAllowedCidrs: cidrs, allowImapSmtp: allowImap, mfaMethods, requireMfa })
                            }
                        >
                            {setSecurity.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save security
                        </Button>
                    </div>
                    {setSecurity.isError ? (
                        <p className="text-[11px] text-destructive">{setSecurity.error?.message}</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function DomainGroup({
    tenantId,
    domain,
    meta,
    users,
    me
}: {
    tenantId: string;
    domain: string;
    meta: MailDomain | null;
    users: TenantUser[];
    me: Me | undefined;
}) {
    const create = useCreateTenantUser(tenantId);
    const update = useUpdateTenantUser(tenantId);
    const remove = useDeleteTenantUser(tenantId);
    const verify = useVerifyDomain(tenantId);
    const removeDomain = useDeleteDomain(tenantId);
    const setSeats = useSetDomainSeatLimit(tenantId);

    const [draft, setDraft] = useState({ local: "", displayName: "", role: "User", password: "" });
    const [editingId, setEditingId] = useState<string | null>(null);
    // Licence editor draft: seats "" = unlimited; tier "" = none; mailGb/fileGb
    // are per-mailbox storage quotas in GiB (blank = keep current); apps is the
    // set of granted app keys.
    const [licenceDraft, setLicenceDraft] = useState<
        { seats: string; tier: string; mailGb: string; fileGb: string; apps: Set<string> } | null
    >(null);

    const allowedRoles = me?.isSuperAdmin ? [...BASE_ROLES, "SuperAdmin"] : BASE_ROLES;
    // seatLimit semantics: undefined meta = unregistered; null = unlimited;
    // 0 = no licence; >0 = cap. Mirrors the BFF's enforcement exactly.
    const seatLimit = meta ? meta.seatLimit : null;
    const unlicenced = meta !== null && seatLimit === 0;
    const seatsFull = seatLimit !== null && seatLimit > 0 && users.length >= seatLimit;
    const canAdd = me?.isSuperAdmin || me?.emailDomain === domain;

    const seatSummary = meta === null
        ? `${users.length} user${users.length === 1 ? "" : "s"} · unregistered domain`
        : seatLimit === null
            ? `${users.length} seat${users.length === 1 ? "" : "s"} · unlimited licence`
            : seatLimit === 0
                ? `${users.length} user${users.length === 1 ? "" : "s"} · no licence`
                : `${users.length} of ${seatLimit} seats`;

    return (
        <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {meta ? (
                        meta.isVerified ? (
                            <CheckCircle className="h-4 w-4 text-emerald-600" aria-label="Verified" />
                        ) : (
                            <Ban className="h-4 w-4 text-amber-500" aria-label="Pending verification" />
                        )
                    ) : (
                        <Ban className="h-4 w-4 text-muted-foreground" aria-label="No mail domain registered" />
                    )}
                    <span className="text-sm font-semibold">@{domain}</span>
                    <span className={`text-xs ${(seatsFull || unlicenced) ? "text-destructive" : "text-muted-foreground"}`}>
                        {seatSummary}
                    </span>
                    {meta?.planTier ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                            {meta.planTier}
                        </span>
                    ) : null}
                </div>
                {me?.isSuperAdmin && meta ? (
                    <div className="flex items-center gap-1">
                        {/* Seed the whole draft from the row on first edit so seats,
                            tier and both quotas stay in sync while typing. */}
                        <Input
                            type="number"
                            min={0}
                            className="h-7 w-16 text-xs"
                            placeholder="∞"
                            title="Licenced seats — leave empty for unlimited, 0 for no licence"
                            value={licenceDraft ? licenceDraft.seats : meta.seatLimit ?? ""}
                            onChange={(e) => setLicenceDraft((d) => ({
                                ...(d ?? seedLicenceDraft(meta)),
                                seats: e.target.value
                            }))}
                            aria-label={`Licenced seats for ${domain} (empty = unlimited)`}
                        />
                        <select
                            className="h-7 rounded-md border bg-background px-1 text-xs"
                            value={licenceDraft ? licenceDraft.tier : meta.planTier ?? ""}
                            onChange={(e) => setLicenceDraft((d) => ({
                                ...(d ?? seedLicenceDraft(meta)),
                                tier: e.target.value
                            }))}
                            aria-label={`Plan tier for ${domain}`}
                        >
                            <option value="">No plan</option>
                            <option value="Basic">Basic</option>
                            <option value="Standard">Standard</option>
                            <option value="Premium">Premium</option>
                        </select>
                        <div className="flex items-center gap-0.5" title="Email storage per mailbox (GB)">
                            <Input
                                type="number"
                                min={0}
                                step={0.5}
                                className="h-7 w-16 text-xs"
                                value={licenceDraft ? licenceDraft.mailGb : bytesToGbStr(meta.mailQuotaBytesPerUser)}
                                onChange={(e) => setLicenceDraft((d) => ({
                                    ...(d ?? seedLicenceDraft(meta)),
                                    mailGb: e.target.value
                                }))}
                                aria-label={`Email storage per mailbox for ${domain} in GB`}
                            />
                            <span className="text-[10px] text-muted-foreground">GB mail</span>
                        </div>
                        <div className="flex items-center gap-0.5" title="File storage per mailbox (GB)">
                            <Input
                                type="number"
                                min={0}
                                step={0.5}
                                className="h-7 w-16 text-xs"
                                value={licenceDraft ? licenceDraft.fileGb : bytesToGbStr(meta.fileQuotaBytesPerUser)}
                                onChange={(e) => setLicenceDraft((d) => ({
                                    ...(d ?? seedLicenceDraft(meta)),
                                    fileGb: e.target.value
                                }))}
                                aria-label={`File storage per mailbox for ${domain} in GB`}
                            />
                            <span className="text-[10px] text-muted-foreground">GB files</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5" title="Apps this plan grants">
                            {SUITE_APPS.map((appKey) => {
                                const draft = licenceDraft ?? seedLicenceDraft(meta);
                                const checked = draft.apps.has(appKey);
                                return (
                                    <label key={appKey} className="flex cursor-pointer items-center gap-0.5 text-[11px] capitalize">
                                        <input
                                            type="checkbox"
                                            className="h-3 w-3"
                                            checked={checked}
                                            onChange={(e) => setLicenceDraft((d) => {
                                                const base = d ?? seedLicenceDraft(meta);
                                                const apps = new Set(base.apps);
                                                if (e.target.checked) apps.add(appKey); else apps.delete(appKey);
                                                return { ...base, apps };
                                            })}
                                            aria-label={`Grant ${appKey} to ${domain}`}
                                        />
                                        {appKey}
                                    </label>
                                );
                            })}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={licenceDraft === null || setSeats.isPending}
                            onClick={() => {
                                if (licenceDraft === null) return;
                                const seats = licenceDraft.seats.trim() === ""
                                    ? null
                                    : Math.max(0, Number(licenceDraft.seats) || 0);
                                setSeats.mutate(
                                    {
                                        domainId: meta.id,
                                        seatLimit: seats,
                                        planTier: licenceDraft.tier || null,
                                        mailQuotaBytesPerUser: gbStrToBytes(licenceDraft.mailGb),
                                        fileQuotaBytesPerUser: gbStrToBytes(licenceDraft.fileGb),
                                        enabledApps: SUITE_APPS.filter((a) => licenceDraft.apps.has(a)).join(",")
                                    },
                                    { onSuccess: () => setLicenceDraft(null) }
                                );
                            }}
                        >
                            <Save className="h-3 w-3" /> Licence
                        </Button>
                        {!meta.isVerified ? (
                            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => verify.mutate(meta.id)}>
                                Verify
                            </Button>
                        ) : null}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                                if (window.confirm(`Remove domain ${domain} from this console?`)) removeDomain.mutate(meta.id);
                            }}
                            aria-label={`Remove ${domain}`}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                ) : null}
            </div>

            {me?.isSuperAdmin && meta ? <DomainSecuritySection tenantId={tenantId} meta={meta} /> : null}

            {canAdd ? (
                <div className="mb-3 grid grid-cols-[1fr_1fr_1fr_auto_auto] gap-2">
                    <div className="flex items-center rounded-md border bg-background pr-2">
                        <input
                            className="h-9 w-full min-w-0 bg-transparent px-3 text-sm outline-none"
                            placeholder="mailbox"
                            value={draft.local}
                            onChange={(e) => setDraft((d) => ({ ...d, local: e.target.value.trim() }))}
                            aria-label={`New mailbox on ${domain}`}
                        />
                        <span className="shrink-0 text-xs text-muted-foreground">@{domain}</span>
                    </div>
                    <Input placeholder="Display name" value={draft.displayName} onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))} />
                    <Input
                        type="password"
                        placeholder="Temp password (12+ chars)"
                        value={draft.password}
                        onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))}
                        autoComplete="new-password"
                    />
                    <select
                        className="h-9 rounded-md border bg-background px-2 text-sm"
                        value={draft.role}
                        onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))}
                        aria-label="Role"
                    >
                        {allowedRoles.map((r) => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                    <Button
                        size="sm"
                        onClick={() => {
                            if (!draft.local || !draft.displayName || !draft.password) return;
                            create.mutate({
                                email: `${draft.local}@${domain}`,
                                displayName: draft.displayName,
                                role: draft.role,
                                password: draft.password,
                                isEnabled: true
                            }, { onSuccess: () => setDraft({ local: "", displayName: "", role: "User", password: "" }) });
                        }}
                        disabled={create.isPending || !draft.local || !draft.displayName || !draft.password || seatsFull || unlicenced}
                        title={unlicenced ? "No licence assigned" : seatsFull ? "Seat limit reached" : undefined}
                    >
                        <Plus className="h-4 w-4" /> Add
                    </Button>
                </div>
            ) : null}
            {unlicenced && canAdd ? (
                <p className="mb-2 text-xs text-destructive">
                    No licence assigned — a super admin must assign seats to this domain before mailboxes can be created.
                </p>
            ) : seatsFull && canAdd ? (
                <p className="mb-2 text-xs text-destructive">
                    Seat limit reached — ask the super admin to raise the licence before adding mailboxes.
                </p>
            ) : null}
            {create.isError ? <p className="mb-2 text-xs text-destructive">{create.error?.message}</p> : null}
            {update.isError ? <p className="mb-2 text-xs text-destructive">{update.error?.message}</p> : null}

            {users.length > 0 ? (
                <ul className="divide-y">
                    {users.map((u) =>
                        editingId === u.id ? (
                            <UserEditRow
                                key={u.id}
                                user={u}
                                allowedRoles={allowedRoles}
                                onCancel={() => setEditingId(null)}
                                onSave={(payload) => {
                                    update.mutate(
                                        { userId: u.id, payload },
                                        { onSuccess: () => setEditingId(null) }
                                    );
                                }}
                                saving={update.isPending}
                            />
                        ) : (
                            <li key={u.id} className="flex items-center justify-between py-2 text-sm">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="truncate font-medium">{u.displayName}</span>
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                                u.isEnabled
                                                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
                                                    : "bg-muted text-muted-foreground"
                                            }`}
                                        >
                                            {u.isEnabled ? "enabled" : "disabled"}
                                        </span>
                                    </div>
                                    <div className="truncate text-xs text-muted-foreground">
                                        {u.email} · {u.role}
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setEditingId(u.id)}
                                        aria-label={`Edit ${u.email}`}
                                        title="Edit"
                                    >
                                        <Edit3 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                            if (window.confirm(`Remove ${u.email}?`)) remove.mutate(u.id);
                                        }}
                                        aria-label={`Remove ${u.email}`}
                                        title="Remove"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </li>
                        )
                    )}
                </ul>
            ) : (
                <p className="text-xs text-muted-foreground">No mailboxes on this domain yet.</p>
            )}
        </div>
    );
}

function UserEditRow({
    user,
    allowedRoles,
    onCancel,
    onSave,
    saving
}: {
    user: TenantUser;
    allowedRoles: string[];
    onCancel: () => void;
    onSave: (payload: { email: string; displayName: string; role: string; isEnabled: boolean }) => void;
    saving: boolean;
}) {
    const [displayName, setDisplayName] = useState(user.displayName);
    const [role, setRole] = useState(user.role);
    const [isEnabled, setIsEnabled] = useState(user.isEnabled);

    return (
        <li className="grid grid-cols-[1fr_auto] items-center gap-2 py-2 text-sm">
            <div className="grid grid-cols-3 gap-2">
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display name" />
                <select
                    className="h-9 rounded-md border bg-background px-2 text-sm"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                >
                    {allowedRoles.includes(role) ? null : (
                        <option value={role}>{role}</option>
                    )}
                    {allowedRoles.map((r) => (
                        <option key={r} value={r}>{r}</option>
                    ))}
                </select>
                <label className="flex items-center gap-2 rounded-md border bg-background px-2 text-xs">
                    <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => setIsEnabled(e.target.checked)}
                    />
                    Enabled
                </label>
            </div>
            <div className="flex gap-1">
                <Button
                    size="sm"
                    onClick={() =>
                        onSave({
                            email: user.email,
                            displayName: displayName.trim() || user.displayName,
                            role: role.trim() || user.role,
                            isEnabled
                        })
                    }
                    disabled={saving}
                    aria-label="Save changes"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={onCancel} aria-label="Cancel edit">
                    <X className="h-4 w-4" />
                </Button>
            </div>
        </li>
    );
}
